import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Plus, Trash2, Edit3, Eye, EyeOff, Save, X, BookOpen, Lightbulb, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = ["general", "tips", "behind-the-scenes", "news", "case-study"];

export default function BlogManagement() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    title: "", slug: "", content: "", excerpt: "", category: "general", published: false, featured_image: "",
  });
  const [error, setError] = useState("");
  const [ideas, setIdeas] = useState([]);
  const [ideasLoading, setIdeasLoading] = useState(false);
  const [showIdeas, setShowIdeas] = useState(false);

  const fetchPosts = async () => {
    try {
      const { data } = await axios.get(`${API}/blog/posts`, { withCredentials: true });
      setPosts(data);
    } catch (err) {
      console.error("Failed:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPosts(); }, []);

  const startNew = () => {
    setForm({ title: "", slug: "", content: "", excerpt: "", category: "general", published: false, featured_image: "" });
    setEditing("new");
    setError("");
  };

  const startEdit = (post) => {
    setForm({
      title: post.title,
      slug: post.slug,
      content: post.content,
      excerpt: post.excerpt || "",
      category: post.category || "general",
      published: post.published || false,
      featured_image: post.featured_image || "",
    });
    setEditing(post.id);
    setError("");
  };

  const handleSave = async () => {
    if (!form.title || !form.content) {
      setError("Title and content are required");
      return;
    }
    setError("");
    try {
      if (editing === "new") {
        await axios.post(`${API}/blog/posts`, form, { withCredentials: true });
      } else {
        await axios.patch(`${API}/blog/posts/${editing}`, form, { withCredentials: true });
      }
      setEditing(null);
      fetchPosts();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to save");
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    try {
      await axios.delete(`${API}/blog/posts/${id}`, { withCredentials: true });
      fetchPosts();
    } catch (err) {
      alert("Failed to delete");
    }
  };

  const togglePublish = async (post) => {
    try {
      await axios.patch(`${API}/blog/posts/${post.id}`, {
        ...post,
        published: !post.published,
      }, { withCredentials: true });
      fetchPosts();
    } catch (err) {
      alert("Failed to update");
    }
  };

  const fetchIdeas = async () => {
    setIdeasLoading(true);
    try {
      const { data } = await axios.get(`${API}/blog-ideas`);
      setIdeas(data.ideas || []);
    } catch (err) {
      console.error("Failed to fetch ideas:", err);
    } finally {
      setIdeasLoading(false);
    }
  };

  const refreshIdeas = async () => {
    setIdeasLoading(true);
    try {
      await axios.post(`${API}/blog-ideas/refresh`);
      setTimeout(fetchIdeas, 3000);
    } catch (err) {
      setIdeasLoading(false);
    }
  };

  const useIdea = (idea) => {
    const categoryMap = { "Industry Tips": "tips", "How-To Guides": "tips", "Trends & Inspiration": "news", "Business Advice": "general" };
    setForm({
      title: idea.title,
      slug: idea.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
      content: `# ${idea.title}\n\n${idea.summary}\n\n## Key Points\n\n- \n- \n- \n\n## Conclusion\n\n`,
      excerpt: idea.summary,
      category: categoryMap[idea.category] || "general",
      published: false,
      featured_image: "",
    });
    setEditing("new");
    setShowIdeas(false);
  };

  /* ── EDITOR VIEW ── */
  if (editing !== null) {
    return (
      <div data-testid="blog-editor" className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
            <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 text-orange-400 shrink-0" />
            {editing === "new" ? "New Post" : "Edit Post"}
          </h2>
          <Button variant="ghost" onClick={() => setEditing(null)} className="text-zinc-400 hover:text-white shrink-0">
            <X className="w-4 h-4 mr-1" /> Cancel
          </Button>
        </div>

        {error && <div className="bg-red-500/10 text-red-400 px-3 py-2 rounded text-sm">{error}</div>}

        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4 sm:p-6 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-400 text-xs">Title *</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value, slug: form.slug || e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 60) })}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  placeholder="Your blog post title"
                  data-testid="blog-title"
                />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">Slug</Label>
                <Input
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  placeholder="url-slug"
                  data-testid="blog-slug"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-400 text-xs">Category</Label>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 text-zinc-300 rounded-lg px-3 py-2 text-sm mt-1"
                  data-testid="blog-category"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>{c.replace(/-/g, " ")}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">Featured Image URL</Label>
                <Input
                  value={form.featured_image}
                  onChange={(e) => setForm({ ...form, featured_image: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  placeholder="https://..."
                />
              </div>
            </div>

            <div>
              <Label className="text-zinc-400 text-xs">Excerpt</Label>
              <Textarea
                value={form.excerpt}
                onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
                rows={2}
                placeholder="Brief summary..."
                data-testid="blog-excerpt"
              />
            </div>

            <div>
              <Label className="text-zinc-400 text-xs">Content * (Markdown supported)</Label>
              <Textarea
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-white font-mono text-sm mt-1"
                rows={15}
                placeholder="Write your blog post here..."
                data-testid="blog-content"
              />
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <label className="flex items-center gap-2 text-sm text-zinc-400 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.published}
                  onChange={(e) => setForm({ ...form, published: e.target.checked })}
                  className="rounded border-zinc-600"
                  data-testid="blog-published"
                />
                Publish immediately
              </label>
              <Button onClick={handleSave} className="bg-gradient-to-r from-orange-500 to-red-600 text-white w-full sm:w-auto" data-testid="save-blog-btn">
                <Save className="w-4 h-4 mr-2" /> Save Post
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  /* ── LIST VIEW ── */
  return (
    <div data-testid="blog-management" className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
          <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 text-orange-400" /> Blog Posts
        </h2>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <Button
            variant="outline"
            onClick={() => { setShowIdeas(!showIdeas); if (!showIdeas && ideas.length === 0) fetchIdeas(); }}
            className="border-zinc-700 text-zinc-300 hover:text-white flex-1 sm:flex-none"
            data-testid="blog-ideas-btn"
          >
            <Lightbulb className="w-4 h-4 mr-2" /> Ideas
          </Button>
          <Button onClick={startNew} className="bg-gradient-to-r from-orange-500 to-red-600 text-white flex-1 sm:flex-none" data-testid="new-post-btn">
            <Plus className="w-4 h-4 mr-2" /> New Post
          </Button>
        </div>
      </div>

      {/* Ideas Panel */}
      {showIdeas && (
        <Card className="bg-zinc-900 border-zinc-800" data-testid="blog-ideas-panel">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-white text-base sm:text-lg flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-yellow-400" /> AI-Generated Blog Ideas
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={refreshIdeas} disabled={ideasLoading} className="text-zinc-400 hover:text-white">
                <RefreshCw className={`w-4 h-4 ${ideasLoading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {ideasLoading && ideas.length === 0 ? (
              <div className="text-zinc-500 text-center py-6 text-sm">Generating ideas...</div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-2">
                {ideas.map((idea, i) => (
                  <div key={`idea-${i}-${idea.title?.substring(0,20)}`} className="bg-zinc-800 rounded-lg p-3 hover:bg-zinc-750 transition group">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium leading-tight">{idea.title}</p>
                        <p className="text-zinc-500 text-xs mt-1 line-clamp-2">{idea.summary}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge className="bg-zinc-700 text-zinc-400 text-[10px]">{idea.category}</Badge>
                          <Badge className={`text-[10px] ${idea.difficulty === 'easy' ? 'bg-green-500/20 text-green-400' : idea.difficulty === 'hard' ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'}`}>{idea.difficulty}</Badge>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => useIdea(idea)} className="text-zinc-500 hover:text-orange-400 shrink-0" title="Use this idea">
                        <Edit3 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Post list */}
      {loading ? (
        <div className="text-zinc-500 text-center py-12">Loading...</div>
      ) : posts.length === 0 ? (
        <div className="text-center py-16">
          <BookOpen className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-400 mb-4">No blog posts yet</p>
          <Button onClick={startNew} variant="outline" className="border-zinc-700 text-zinc-300">Create your first post</Button>
        </div>
      ) : (
        <div className="space-y-2">
          {posts.map((post) => (
            <Card key={post.id} className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="text-white font-medium">{post.title}</span>
                      <Badge className={post.published ? "bg-green-500/20 text-green-400" : "bg-zinc-700 text-zinc-400"}>
                        {post.published ? "Published" : "Draft"}
                      </Badge>
                      <Badge className="bg-zinc-800 text-zinc-500 text-xs">{post.category}</Badge>
                    </div>
                    <p className="text-xs text-zinc-500 line-clamp-1">{post.excerpt || post.content?.substring(0, 100)}</p>
                    <p className="text-xs text-zinc-600 mt-1">
                      by {post.author_name} &middot; {new Date(post.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => togglePublish(post)} className="text-zinc-400 hover:text-white" title={post.published ? "Unpublish" : "Publish"}>
                      {post.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => startEdit(post)} className="text-zinc-400 hover:text-white" data-testid={`edit-post-${post.id}`}>
                      <Edit3 className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(post.id, post.title)} className="text-zinc-400 hover:text-red-400" data-testid={`delete-post-${post.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
